import { supertypeClass } from '@haventech/amorphic';
import { readFile } from 'fs';
import { promisify } from 'util';
import { IllustrationRequest } from './IllustrationRequest';
import { StateSpecification } from './basicIllustration/templates/common/StateSpecification';
import { constants } from './basicIllustration/templates/constantsForPdf';
import { CoverpathUtils } from '../../js/static/CoverpathUtils';
import { Product, RiderType } from '../../../common/js/static/Product';
import { Utils } from '../../../common/js/Utils';
import { VantageTermECPFormNumbers } from './HtmlPdfTypes';
import { PolicyRider } from '../../../common/js/customer/PolicyRider';
import { wholeLifeColumns } from './IfiConstants';

@supertypeClass({ toClient: false, toServer: false })
export class HtmlPdfUtils {
  /**
   *  Returns a group of HTML that is then
   * @param baseDirectory:  string, @param templateFiles: any - hard to make a type for this as it changes based on componet
   */
  static getHtmlContent(style: string, body: string) {
    return `
            <head>
                <style>
                    ${style}
                </style>
            </head>
            <body>
                <div class="page-wrapper">
                    ${body}
                </div>
            </body>
            `;
  }

  /**
   *  Pulls in HTML and CSS files into the templateFiles and returned when given a string to the absolute directory. Names are pulled based on the attributes names on the templateFiles object
   * @param baseDirectory:  string, @param templateFiles: any - hard to make a type for this as it changes based on componet
   */
  static async getContent(baseDirectory: string, templateFiles) {
    const readFileAsync = promisify(readFile);

    let keys = Object.keys(templateFiles);

    await Promise.all(
      keys.map(async (key) => {
        if (key === 'css' || key === 'isType') {
          return;
        }

        templateFiles[key] = await readFileAsync(baseDirectory + key + '.html', { encoding: 'utf-8' });
      })
    );

    keys = Object.keys(templateFiles.css);

    await Promise.all(
      keys.map(async (key) => {
        templateFiles.css[key] = await readFileAsync(baseDirectory + key + '.css', { encoding: 'utf-8' });
      })
    );

    return templateFiles;
  }

  /**
   *  Get the policy number based on state and product code
   * @param state string
   * @param productCode string
   */
  static getPolicyFormNumber(state: string, productCode: string) {
    // MMTRADWL100 uses different policy form numbers
    if (productCode === Product.products.MMTRADWL100.code) {
      const formNumber = constants.MMTRADWL100PolicyFormNumbers[state];
      if (!formNumber) {
        return constants.MMTRADWL100GenericPolicyFormNumber;
      }

      return formNumber;
    }

    switch (state) {
      case 'FL':
        return constants.PolicyFormNumberForFlorida;
      case 'CA':
        return constants.PolicyFormNumberForCalifornia;
      case 'DE':
        return constants.PolicyFormNumberForDeleware;
      case 'ND':
        return constants.PolicyFormNumberForNorthDakota;
      case 'SD':
        return constants.PolicyFormNumberForSouthDakota;
      case 'NY':
        return constants.PolicyFormNumberForNewYork;
      default:
        return constants.PolicyFormNumber;
    }
  }

  /**
   * Get the policy form number for a Vantage Term policy based on state
   * @param state
   */
  static getVantageTermPolicyFormNumber(state: string, productBlock?: string) {
    if (productBlock === 'MMTRADTerm_2020b1' || productBlock === 'MMTRADTerm_2018b1') {
      switch (state) {
        case 'DE':
          return 'TL-2018';
        case 'CA':
          return 'TL-CA-2018';
        case 'DC':
          return 'TL-DC-2018';
        case 'FL':
          return 'TL-FL-2018';
        case 'NY':
          return 'TL-NY-2018';
        case 'ND':
          return 'TL-ND-2018';
        case 'SD':
          return 'TL-SD-2018';
        default:
          return 'ICC18TL';
      }
    } else if (productBlock === 'MMOPMTerm_2004b1') {
      switch (state) {
        case 'AR':
        case 'AZ':
        case 'CT':
        case 'FL':
        case 'KS':
        case 'MN':
        case 'MO':
        case 'MT':
        case 'NY':
        case 'NC':
        case 'OR':
        case 'PA':
        case 'SD':
        case 'TX':
        case 'VA':
        case 'WI':
          return `404-${state}-9300`;
        case 'CA':
        case 'CO':
          return `404-${state}-2000`;
        case 'LA':
        case 'MA':
        case 'NJ':
        case 'SC':
        case 'WA':
          return `404-${state}-2004G20`;
        case 'UT':
          return '404-UT-2010';
        default:
          return '404-9300';
      }
    } else if (productBlock === 'MMOPMTerm_2005b1') {
      switch (state) {
        case 'AR':
        case 'AZ':
        case 'CA':
        case 'CO':
        case 'CT':
        case 'FL':
        case 'GA':
        case 'ID':
        case 'IL':
        case 'IN':
        case 'KS':
        case 'ME':
        case 'MD':
        case 'MN':
        case 'MO':
        case 'MT':
        case 'NE':
        case 'NY':
        case 'NC':
        case 'NC':
        case 'OK':
        case 'OR':
        case 'PA':
        case 'PR':
        case 'SD':
        case 'TX':
        case 'UT':
        case 'VT':
        case 'VA':
        case 'WI':
          return `408-${state}-2005`;
        case 'LA':
        case 'MA':
        case 'NJ':
        case 'SC':
        case 'WA':
          return `408-${state}-2005G20`;
        default:
          return '408-2005';
      }
    } else if (productBlock === 'MMOPMTermAPT80_2006b1') {
      switch (state) {
        case 'AR':
        case 'AZ':
        case 'CA':
        case 'CO':
        case 'CT':
        case 'DC':
        case 'FL':
        case 'GA':
        case 'ID':
        case 'IL':
        case 'IN':
        case 'KS':
        case 'LA':
        case 'ME':
        case 'MD':
        case 'MA':
        case 'MN':
        case 'MO':
        case 'MT':
        case 'NE':
        case 'NV':
        case 'NH':
        case 'NY':
        case 'NJ':
        case 'NC':
        case 'ND':
        case 'OH':
        case 'OK':
        case 'OR':
        case 'PA':
        case 'PR':
        case 'SC':
        case 'SD':
        case 'TN':
        case 'TX':
        case 'UT':
        case 'VT':
        case 'VA':
        case 'WA':
        case 'WI':
          return `410-${state}-2006`;
        default:
          return '410-2006';
      }
    } else if (productBlock === 'MMOPMTerm_2000b1' || productBlock === 'MMOPMTermAPT80_2000b1') {
      switch (state) {
        case 'AR':
        case 'AZ':
        case 'CT':
        case 'FL':
        case 'KS':
        case 'MN':
        case 'MO':
        case 'MT':
        case 'NY':
        case 'NJ':
        case 'NC':
        case 'OR':
        case 'PA':
        case 'SD':
        case 'TX':
        case 'VA':
        case 'WA':
        case 'WI':
          return `404-${state}-9300`;
        case 'CA':
        case 'CO':
          return `404-${state}-2000`;
        case 'MA':
        case 'SC':
          return `404-${state}-9900`;
        case 'UT':
          return '404-UT-2010';
        default:
          return '404-9300';
      }
    } else {
      switch (state) {
        case 'AL':
          return 'TL-AL-2009';
        case 'AR':
          return 'TL-AR-2009';
        case 'AZ':
          return 'TL-AZ-2009';
        case 'CT':
          return 'TL-CT-2009';
        case 'DC':
          return 'TL-DC-2009';
        case 'IL':
          return 'TL-IL-2009a';
        case 'NJ':
          return 'TL-NJ-2009';
        case 'OR':
          return 'TL-OR-2009';
        case 'DE':
          return 'TL-DE-2009';
        case 'CA':
          return 'TL-CA-2009';
        case 'FL':
          return 'TL-FL-2009';
        case 'ND':
          return 'TL-ND-2009';
        case 'NY':
          return 'TL-NY-2009';
        case 'SD':
          return 'TL-SD-2009';
        default:
          return 'ICC09TL';
      }
    }
  }

  static getVantageTermECPPolicyFormNumber(state: string) {
    if (!Object.keys(VantageTermECPFormNumbers).includes(state)) {
      return VantageTermECPFormNumbers['ICC'];
    }
    return VantageTermECPFormNumbers[state];
  }

  /**
   * Get the policy form number for a whole life policy based on state
   * @param state
   */
  static getWholeLifePolicyFormNumber(state: string) {
    switch (state) {
      case 'DE':
        return 'WL-DE-2018';
      case 'CA':
        return 'WL-CA-2018';
      case 'FL':
        return 'WL-FL-2018';
      case 'ND':
        return 'WL-ND-2018';
      case 'SD':
        return 'WL-SD-2018';
      default:
        return 'ICC18WL';
    }
  }

  /**
   * Get the policy form number for a CAS Term policy based on state
   * @param state
   */
  static getCASTermPolicyFormNumber(state: string) {
    switch (state) {
      case 'DE':
        return 'SCT-DE 0818';
      case 'CA':
        return 'SCT-CA 0818';
      case 'FL':
        return 'SCT-FL 0519';
      case 'ND':
        return 'SCT-ND 0818';
      case 'SD':
        return 'SCT-SD 0818';
      default:
        return 'ICC18TFC';
    }
  }

  /**
   * Returns gender in the full name or unixsex based on if the state allows it
   * @param gender string, @param state string
   */
  static getGenderInPrettyFormat(gender: string | number, state: string) {
    if (CoverpathUtils.isUnisexState(Product.channels.CAS.code, state)) {
      return 'Unisex';
    }

    switch (gender) {
      case 1:
      case '1':
      case 'M':
      case 'm':
        return 'Male';
      case 2:
      case '2':
      case 'F':
      case 'f':
        return 'Female';
      default:
        return gender;
    }
  }
  /**
   * Returns html that displays the agent's phone number
   * @param illustrationRequest
   */
  static getAgentPhone(illustrationRequest: IllustrationRequest): string {
    if (StateSpecification.PhoneNumberRequiredStates.includes(illustrationRequest.state)) {
      const agentPhone = illustrationRequest.agent.phone;
      if (agentPhone) {
        return `<p class="p3 ft3">Agent Telephone Number: ${agentPhone} </p>`;
      }
    }
    return '';
  }

  static getDateInPrettyFormat() {
    const date = new Date();
    const formatter = new Intl.DateTimeFormat('en', { month: 'long' });
    return formatter.format(date) + ' ' + date.getDate() + ', ' + date.getFullYear();
  }

  static getDateInPrettyFormatFromString(dateString: string) {
    const date = new Date(dateString);
    const formatter = new Intl.DateTimeFormat('en', { month: 'long' });
    return formatter.format(date) + ' ' + date.getDate() + ', ' + date.getFullYear();
  }

  /**
   * Returns true if insured is juvenile
   * @param illustrationRequest
   */
  static isInsuredJuvenile(illustrationRequest: IllustrationRequest): boolean {
    const insuredDob = illustrationRequest.dob;
    const asOfDate = illustrationRequest.asOfDate || Utils.now();
    const insuranceAgeDate = Utils.getCurrentMidYearDate(insuredDob, asOfDate);
    const insuredAge = Utils.yearsBetween(insuredDob, asOfDate) + (insuranceAgeDate <= asOfDate ? 1 : 0);
    // For illustration, we use 14 (NOT 18) as the up limit for juvenile. If insured is < 14, show rate class as "Juvenile" instead
    // of the real rate class.
    return insuredAge < 14;
  }

  static isCASProduct(productCode: string): boolean {
    return productCode === Product.products.CASTerm.code || productCode === Product.products.CASTermECP.code;
  }

  static getSignaturePageNumber(
    riders: PolicyRider[],
    dividendOption: string,
    waiverUnits: number,
    cashValueTransfer: number,
    isMec: boolean
  ): number {
    const hasWOP = waiverUnits > 0;
    const hasGIR = !!riders.find((rider) => rider.type === RiderType.GIR);
    const hasRTR = !!riders.find((rider) => rider.type === RiderType.RTR);
    const has1035 = cashValueTransfer > 0;
    const hasScheduledALIR = !!riders.find((rider) => rider.type === RiderType.ALIR) && !has1035;
    const hasCIR = !!riders.find((rider) => rider.type === RiderType.CIR);

    const linesPerPage = 55;
    const pageOneBase = 49;
    const pageTwoBase = 47;

    // page 1 additions
    let lineCount = pageOneBase;

    // riders
    lineCount += hasWOP ? 1 : 0;
    lineCount += hasGIR ? 1 : 0;
    lineCount += hasRTR ? 2 : 0;
    lineCount += hasScheduledALIR ? 2 : 0;
    lineCount += has1035 ? 5 : 0;
    lineCount += hasCIR ? 7 : 0;
    lineCount += hasCIR && hasWOP ? 3 : 0;

    // resetting lineCount for page 2 and adding lines overflown from page 1
    lineCount = lineCount > 54 ? 3 : 0;
    lineCount += lineCount > 57 ? 3 : 0;
    lineCount += lineCount > 59 ? 4 : 0;
    lineCount += lineCount > 62 ? 1 : 0;
    lineCount += lineCount > 63 ? 2 : 0;
    lineCount += lineCount > 65 ? 1 : 0;
    lineCount += lineCount > 66 ? 1 : 0;

    // page 2 additions
    lineCount += pageTwoBase;

    // dividend options
    lineCount -= dividendOption === 'Cash' ? 3 : 0;
    lineCount += dividendOption === 'PRPUA' ? 1 : 0;
    lineCount += dividendOption === 'Accumulate' ? 2 : 0;

    // riders
    lineCount += hasWOP ? 7 : 0;
    lineCount += hasGIR ? 3 : 0;
    lineCount += hasRTR ? 5 : 0;
    lineCount += hasScheduledALIR ? 10 : 0;
    lineCount += has1035 ? 12 : 0;
    lineCount += hasCIR ? 20 : 0;

    // tax information
    lineCount -= isMec ? 1 : 0;

    let signaturePageNumber = constants.PagesBeforeTabular;
    signaturePageNumber += Math.ceil(lineCount / linesPerPage) - 1;
    signaturePageNumber += hasCIR ? 1 : 0;

    const meetsSplCond =
      (hasWOP && hasRTR && has1035 && hasCIR) ||
      (hasWOP &&
        hasRTR &&
        hasScheduledALIR &&
        hasCIR &&
        (dividendOption === 'Accumulate' || dividendOption === 'PRPUA'));
    signaturePageNumber += meetsSplCond ? 1 : 0;

    return signaturePageNumber;
  }

  static buildColumnHeaderText(tableColumns: string[], isSupplemental: boolean = false): string {
    let columnHeaderText = `<TR>
        <TH><P>&nbsp;</P></TH>
        <TH><P>&nbsp;</P></TH>
        <TH colspan=${
          tableColumns.length - 2
        } class="td-underline"><P class="p-center"><NOBR>Non-Guaranteed</NOBR> Values*</P></TH>
       </TR>`;

    for (let i = 0; i < 4; i++) {
      let rowString = `<TR class="${i === 3 ? `td-underline ` : ``}illustration-header-row">`;
      for (const col of tableColumns) {
        const supplementalOverride = wholeLifeColumns[col].supplementalOverride;
        const colDefinition =
          isSupplemental && supplementalOverride ? wholeLifeColumns[supplementalOverride] : wholeLifeColumns[col];
        rowString += `<TH><P>${colDefinition.headerText[i]}</P></TH>`;
      }

      rowString += `</TR>`;
      columnHeaderText += rowString;
    }

    return columnHeaderText;
  }
}
